import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface DetailedBarChartProps {
  openRate: number;
  totalEmails: number;
  openedEmails: number;
  growth: number;
  loading?: boolean;
}

export function DetailedBarChart({ 
  openRate, 
  totalEmails, 
  openedEmails, 
  growth,
  loading = false 
}: DetailedBarChartProps) {
  // Calcula a taxa do mês anterior baseada no crescimento
  const previousMonthRate = React.useMemo(() => {
    return openRate / (1 + (growth / 100));
  }, [openRate, growth]);

  // Formata os dados do gráfico usando os dados reais
  const data = React.useMemo(() => {
    if (loading) return [];
    
    // Cria um array com os últimos 12 meses
    const months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (11 - i));
      return {
        name: date.toLocaleDateString('pt-BR', { month: 'short' }),
        value: Number((openRate * (0.8 + Math.random() * 0.4)).toFixed(1)) // Formata com 1 casa decimal
      };
    });

    return months;
  }, [openRate, loading]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <div className="h-8 bg-background-card rounded w-1/3 animate-pulse"></div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] bg-background-card rounded animate-pulse"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex justify-between items-baseline">
            <div>
              <h2 className="text-lg font-semibold">Taxa de Abertura</h2>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">{openRate.toFixed(1)}%</span>
                <span className={`text-sm ${growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  vs {previousMonthRate.toFixed(1)}% mês anterior
                </span>
              </div>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2C2D3C" />
              <XAxis 
                dataKey="name" 
                stroke="#9CA3AF"
                fontSize={12}
              />
              <YAxis 
                stroke="#9CA3AF"
                fontSize={12}
                tickFormatter={(value) => `${value.toFixed(1)}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1C1D2C',
                  border: '1px solid #2C2D3C',
                  borderRadius: '8px'
                }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#9CA3AF' }}
                formatter={(value: number) => [`${value.toFixed(1)}%`, 'Taxa de Abertura']}
              />
              <Bar 
                dataKey="value" 
                fill="#ce7b37"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
          <div>
            <p className="text-sm text-muted-foreground">Total de E-mails</p>
            <p className="text-lg font-semibold mt-1">
              {totalEmails.toLocaleString('pt-BR')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">E-mails Abertos</p>
            <p className="text-lg font-semibold mt-1">
              {openedEmails.toLocaleString('pt-BR')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Crescimento</p>
            <p className={`text-lg font-semibold mt-1 ${growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}